// frontend/src/pages/admin/Photos.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import { useCouple } from '../../contexts/CoupleContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import PhotoService from '../../services/photo.service';
import UploadService from '../../services/upload.service';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Loader from '../../components/common/Loader';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Textarea from '../../components/common/Textarea';
import { Image, Plus, Edit2, Trash2, Star, AlertCircle } from 'react-feather';
import { Photo as PhotoType } from '../../types';
import { AxiosError } from 'axios';

const Photos: React.FC = () => {
  const { currentCouple } = useCouple();
  
  const [photos, setPhotos] = useState<PhotoType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [currentPhoto, setCurrentPhoto] = useState<PhotoType | null>(null);
  
  // Track which images failed to load
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  
  // Form state
  const [title, setTitle] = useState<string>('');
  const [caption, setCaption] = useState<string>('');
  const [featured, setFeatured] = useState<boolean>(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  
  useEffect(() => {
    if (currentCouple) {
      fetchPhotos();
    }
  }, [currentCouple]);
  
  const fetchPhotos = async () => {
    if (!currentCouple) return;
    
    try {
      setLoading(true);
      const fetchedPhotos = await PhotoService.getCouplePhotos(currentCouple._id);
      setPhotos(fetchedPhotos);
      setError(null);
      
      // Reset failed images when fetching new photos
      setFailedImages({});
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch photos';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      
      // Quick validation of file types and sizes
      const validFiles = files.filter(file => {
        const isImage = file.type.startsWith('image/');
        const isSizeValid = file.size <= 5 * 1024 * 1024; // 5MB limit
        
        if (!isImage) {
          setError(`File "${file.name}" is not an image.`);
        } else if (!isSizeValid) {
          setError(`File "${file.name}" exceeds the 5MB size limit.`);
        }
        
        return isImage && isSizeValid;
      });
      
      setImageFiles(validFiles);
    }
  };
  
  const handleAddPhotos = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!currentCouple || imageFiles.length === 0) return;
    
    setIsUploading(true);
    
    try {
      // Upload single image
      console.log('Uploading image:', imageFiles[0].name);
      const uploadedUrl = await UploadService.uploadImage(imageFiles[0], 'photos');
      console.log('Uploaded URL:', uploadedUrl);
      
      // Create photo record
      const photo = await PhotoService.createPhoto({
        url: uploadedUrl,
        title,
        caption: caption || undefined,
        featured,
        couple: currentCouple._id
      }).then(photo => {
        console.log('Created photo:', photo);
        return photo;
      }).catch(err => {
        console.error('Failed to create photo for URL:', uploadedUrl, 'Error:', err);
        throw err;
      });
      
      resetForm();
      setIsAddModalOpen(false);
      fetchPhotos();
    } catch (err) {
      let errorMessage = 'Failed to upload photo. Please try again.';
      if (err instanceof AxiosError) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          errorMessage = 'User not authenticated. Please log in.';
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message; // e.g., "Free users can only upload up to 5 images..."
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      console.error('Add photo error:', err);
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleEditPhoto = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!currentCouple || !currentPhoto) return;
    
    setIsUploading(true);
    
    try {
      await PhotoService.updatePhoto(currentPhoto._id, {
        title,
        caption: caption || undefined,
        featured
      });
      
      resetForm();
      setIsEditModalOpen(false);
      fetchPhotos();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update photo';
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleDeletePhoto = async () => {
    if (!currentPhoto) return;
    
    setIsUploading(true);
    
    try {
      await PhotoService.deletePhoto(currentPhoto._id);
      setIsDeleteModalOpen(false);
      fetchPhotos();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete photo';
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleToggleFeatured = async (photo: PhotoType) => {
    try {
      await PhotoService.updatePhoto(photo._id, {
        featured: !photo.featured
      });
      
      fetchPhotos();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update photo';
      setError(errorMessage);
    }
  };
  
  const handleImageError = (photoId: string) => {
    setFailedImages(prev => ({
      ...prev,
      [photoId]: true
    }));
  };
  
  // Get an optimized image URL for display
  const getDisplayUrl = (url: string): string => {
    return UploadService.getOptimizedImageUrl(url);
  };
  
  const openEditModal = (photo: PhotoType) => {
    setCurrentPhoto(photo);
    setTitle(photo.title || '');
    setCaption(photo.caption || '');
    setFeatured(photo.featured);
    setIsEditModalOpen(true);
  };
  
  const openDeleteModal = (photo: PhotoType) => {
    setCurrentPhoto(photo);
    setIsDeleteModalOpen(true);
  };
  
  const resetForm = () => {
    setTitle('');
    setCaption('');
    setFeatured(false);
    setImageFiles([]);
    setCurrentPhoto(null);
  };
  
  if (!currentCouple) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10">
          <p className="text-center text-gray-500">Please create a couple profile first.</p>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10">
        <div className="md:flex md:items-center md:justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Wedding Photos</h2>
            <p className="mt-1 text-sm text-gray-500">
              Upload and manage your wedding photos
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Button 
              onClick={() => {
                resetForm();
                setIsAddModalOpen(true);
              }}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              Add Photos
            </Button>
          </div>
        </div>
        
        {error && (
          <Alert
            type="error"
            message={error}
            dismissible
            onDismiss={() => setError(null)}
            className="mb-6"
          />
        )}
        
        {loading ? (
          <div className="text-center py-12">
            <Loader size="lg" label="Loading photos..." />
          </div>
        ) : photos.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <Image className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">No photos added yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Start adding photos to showcase your wedding journey.
            </p>
            <div className="mt-6">
              <Button 
                onClick={() => {
                  resetForm();
                  setIsAddModalOpen(true);
                }}
              >
                Upload Your First Photos
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {photos.map((photo) => (
              <div key={photo._id} className="bg-white shadow rounded-lg overflow-hidden">
                <div className="relative aspect-[4/3] bg-gray-200">
                  {failedImages[photo._id] ? (
                    <div className="h-full w-full flex items-center justify-center">
                      <div className="text-center p-4">
                        <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Image could not be loaded</p>
                      </div>
                    </div>
                  ) : (
                    <img 
                      src={getDisplayUrl(photo.url)} 
                      alt={photo.title || 'Wedding photo'} 
                      className="h-full w-full object-cover"
                      onError={() => handleImageError(photo._id)}
                      loading="lazy"
                    />
                  )}
                  <button
                    className={`absolute top-2 right-2 p-1 rounded-full ${
                      photo.featured ? 'bg-yellow-400 text-white' : 'bg-white text-gray-500'
                    }`}
                    onClick={() => handleToggleFeatured(photo)}
                    title={photo.featured ? 'Featured photo' : 'Mark as featured'}
                  >
                    <Star className="h-4 w-4" />
                  </button>
                </div>
                <div className="p-4">
                  {photo.title && (
                    <h3 className="text-lg font-medium text-gray-900">{photo.title}</h3>
                  )}
                  {photo.caption && (
                    <p className="mt-1 text-sm text-gray-500">{photo.caption}</p>
                  )}
                  <div className="mt-4 flex justify-end space-x-2">
                    <button
                      onClick={() => openEditModal(photo)}
                      className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openDeleteModal(photo)}
                      className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Add Photos Modal */}
        <Modal
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          title="Add Photos"
        >
          <form onSubmit={handleAddPhotos} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Upload Images</label>
              <div className="mt-1 flex items-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  multiple
                  className="focus:ring-pink-500 focus:border-pink-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  required
                />
              </div>
              {imageFiles.length > 0 && (
                <p className="mt-1 text-sm text-gray-500">
                  {imageFiles.length} {imageFiles.length === 1 ? 'file' : 'files'} selected
                </p>
              )}
            </div>
            
            <Input
              id="photo-title"
              label="Title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            
            <Textarea
              id="photo-caption"
              label="Caption (optional)"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
            
            <div className="flex items-center">
              <input
                id="featured"
                name="featured"
                type="checkbox"
                checked={featured}
                onChange={(e) => setFeatured(e.target.checked)}
                className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
              />
              <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
                Mark as featured (will appear on wedding homepage)
              </label>
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsAddModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isUploading}
                disabled={isUploading || imageFiles.length === 0}
              >
                Upload Photos
              </Button>
            </div>
          </form>
        </Modal>
        
        {/* Edit Photo Modal */}
        <Modal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          title="Edit Photo"
        >
          <form onSubmit={handleEditPhoto} className="space-y-4">
            {currentPhoto && (
              <div className="mb-4">
                {failedImages[currentPhoto._id] ? (
                  <div className="h-48 w-full bg-gray-100 flex items-center justify-center rounded-md">
                    <div className="text-center">
                      <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Image could not be loaded</p>
                    </div>
                  </div>
                ) : (
                  <img 
                    src={getDisplayUrl(currentPhoto.url)} 
                    alt={currentPhoto.title || 'Wedding photo'} 
                    className="h-48 w-full object-cover rounded-md"
                    onError={() => handleImageError(currentPhoto._id)}
                  />
                )}
              </div>
            )}
            
            <Input
              id="edit-photo-title"
              label="Title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            
            <Textarea
              id="edit-photo-caption"
              label="Caption (optional)"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
            
            <div className="flex items-center">
              <input
                id="edit-featured"
                name="featured"
                type="checkbox"
                checked={featured}
                onChange={(e) => setFeatured(e.target.checked)}
                className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
              />
              <label htmlFor="edit-featured" className="ml-2 block text-sm text-gray-900">
                Mark as featured (will appear on wedding homepage)
              </label>
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsEditModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isUploading}
                disabled={isUploading}
              >
                Save Changes
              </Button>
            </div>
          </form>
        </Modal>
        
        {/* Delete Confirmation Modal */}
        <Modal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Delete Photo"
        >
          <div className="space-y-4">
            {currentPhoto && (
              <div className="mb-4">
                {failedImages[currentPhoto._id] ? (
                  <div className="h-48 w-full bg-gray-100 flex items-center justify-center rounded-md">
                    <div className="text-center">
                      <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Image could not be loaded</p>
                    </div>
                  </div>
                ) : (
                  <img 
                    src={getDisplayUrl(currentPhoto.url)} 
                    alt={currentPhoto.title || 'Wedding photo'} 
                    className="h-48 w-full object-cover rounded-md" 
                    onError={() => handleImageError(currentPhoto._id)}
                  />
                )}
              </div>
            )}
            
            <p className="text-sm text-gray-500">
              Are you sure you want to delete this photo? This action cannot be undone.
            </p>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeletePhoto}
                loading={isUploading}
                disabled={isUploading}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default Photos;